import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useRef } from 'react';
import {
  Controller,
  useFieldArray,
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';

import { Button } from '@/components/ui/Button';
import { CUSTOM_PLATFORM, KNOWN_PLATFORMS } from '@/lib/socialLinks';

interface SocialLinksEditorProps<T extends FieldValues> {
  /** The array field's name, e.g. `social_links`. */
  name: ArrayPath<T>;
  control: Control<T>;
  errors: FieldErrors<T>;
  disabled?: boolean;
}

/** One row's message for one of its three parts. */
function rowMessage<T extends FieldValues>(
  errors: FieldErrors<T>,
  name: string,
  index: number,
  part: 'platform' | 'custom' | 'url',
): string | undefined {
  const list: unknown = errors[name as keyof FieldErrors<T>];
  const rows = Array.isArray(list) ? (list as unknown[]) : [];
  const row = rows[index] as Record<string, { message?: unknown }> | undefined;
  const message = row?.[part]?.message;
  return typeof message === 'string' ? message : undefined;
}

/**
 * An organization's social links, as rows that can be added and removed.
 *
 * Rows rather than a fixed set of nine boxes: most organizations use two or
 * three networks, a wall of empty fields for the rest reads as work not done,
 * and the backend accepts platforms nobody has listed here yet.
 *
 * Every control carries the row's number in its label. Two boxes both called
 * "Address" would leave a screen reader user with no way to tell which link
 * they are editing, and a test with no way to find a particular one.
 */
export function SocialLinksEditor<T extends FieldValues>({
  name,
  control,
  errors,
  disabled,
}: SocialLinksEditorProps<T>) {
  const { fields, append, remove } = useFieldArray<T>({ control, name });

  /*
   * Focus has to move to the row that was just added, and the row does not
   * exist until React has rendered it. The callback ref fires when it does; it
   * is armed by Add and disarmed the moment it fires, so a later re-render for
   * an unrelated reason does not steal focus back.
   */
  const focusNewRow = useRef(false);
  const addButton = useRef<HTMLButtonElement>(null);
  const lastRemoveButton = useRef<HTMLButtonElement>(null);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {fields.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No social links yet.
        </Typography>
      )}

      {fields.map((row, index) => (
        <Box
          key={row.id}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            alignItems: { sm: 'flex-start' },
          }}
        >
          <Controller
            name={`${name}.${index}.platform` as Path<T>}
            control={control}
            render={({ field }) => (
              <TextField
                select
                label={`Platform ${index + 1}`}
                sx={{ minWidth: 180 }}
                disabled={disabled}
                {...field}
                value={field.value ?? ''}
                inputRef={(element: HTMLInputElement | null) => {
                  if (element && focusNewRow.current) {
                    focusNewRow.current = false;
                    element.focus();
                  }
                }}
                error={Boolean(rowMessage(errors, name, index, 'platform'))}
                helperText={rowMessage(errors, name, index, 'platform')}
              >
                {KNOWN_PLATFORMS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
                <MenuItem value={CUSTOM_PLATFORM}>Other…</MenuItem>
              </TextField>
            )}
          />

          {/* Only when the platform is not one of the nine. Shown always, it
              would be a box that does nothing on nine choices out of ten. */}
          <CustomKeyField
            name={name}
            index={index}
            control={control}
            errors={errors}
            disabled={disabled}
          />

          <Controller
            name={`${name}.${index}.url` as Path<T>}
            control={control}
            render={({ field }) => (
              <TextField
                label={`Address ${index + 1}`}
                placeholder="https://example.org/org"
                sx={{ flex: 1 }}
                disabled={disabled}
                {...field}
                value={field.value ?? ''}
                error={Boolean(rowMessage(errors, name, index, 'url'))}
                helperText={rowMessage(errors, name, index, 'url')}
              />
            )}
          />

          <IconButton
            aria-label={`Remove link ${index + 1}`}
            disabled={disabled}
            ref={index === fields.length - 1 ? lastRemoveButton : undefined}
            onClick={() => {
              remove(index);
              // Focus must land somewhere deliberate, or it falls to the body
              // and a keyboard user starts again from the top of the page.
              const previous = index > 0 ? index - 1 : null;
              window.setTimeout(() => {
                if (previous === null) addButton.current?.focus();
                else lastRemoveButton.current?.focus();
              }, 0);
            }}
            sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
          >
            <span aria-hidden>×</span>
          </IconButton>
        </Box>
      ))}

      <Box>
        <Button
          type="button"
          variant="secondary"
          ref={addButton}
          disabled={disabled}
          startIcon={<AddIcon />}
          onClick={() => {
            focusNewRow.current = true;
            append({ platform: '', custom: '', url: '' } as FieldArray<T, ArrayPath<T>>);
          }}
        >
          Add link
        </Button>
      </Box>
    </Box>
  );
}

/** The custom key box, which only exists while the platform is "Other…". */
function CustomKeyField<T extends FieldValues>({
  name,
  index,
  control,
  errors,
  disabled,
}: {
  name: ArrayPath<T>;
  index: number;
  control: Control<T>;
  errors: FieldErrors<T>;
  disabled?: boolean;
}) {
  return (
    <Controller
      name={`${name}.${index}.platform` as Path<T>}
      control={control}
      render={({ field: platform }) => {
        if (platform.value !== CUSTOM_PLATFORM) return <></>;

        return (
          <Controller
            name={`${name}.${index}.custom` as Path<T>}
            control={control}
            render={({ field }) => (
              <TextField
                label={`Platform name ${index + 1}`}
                sx={{ minWidth: 180 }}
                disabled={disabled}
                {...field}
                value={field.value ?? ''}
                error={Boolean(rowMessage(errors, name, index, 'custom'))}
                helperText={rowMessage(errors, name, index, 'custom')}
              />
            )}
          />
        );
      }}
    />
  );
}
